import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTrips } from '../../context/TripsContext';
import { useExpenses } from '../../context/ExpensesContext';
import { useAuth } from '../../context/AuthContext';
import { FileText, Plane, Hotel, Receipt, DollarSign, CreditCard, Landmark, User } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RefundReportSection, RefundReportSkeleton, RefundItemCard, ExportButtons, RefundReportTotals, RefundReportSummary } from './components';
import { SpreadsheetTable } from './components/SpreadsheetTable';
import { PerDiemRatesInfo } from './components/PerDiemRatesInfo';
import { useRefundReport, type PerDiem } from '../../context/RefundReportContext';
import { flightDetailsService, businessService } from '../../services/supabase';
import type { Business } from '../../services/supabase/business.service';

// Payment method options with icons - dynamic based on trip setting
const getPaymentMethods = (onlyCooperateCard: boolean) => {
  if (onlyCooperateCard) {
    // When only cooperate card is enabled, show "Cooperate Card" but store as "Card"
    return [
      { value: 'Bank Transfer', icon: Landmark },
      { value: 'Card', icon: CreditCard, displayValue: 'Cooperate Card' },
      { value: 'Personal', icon: User },
    ];
  } else {
    // When both personal and cooperate cards are allowed
    return [
      { value: 'Bank Transfer', icon: Landmark },
      { value: 'Card', icon: CreditCard },
      { value: 'Cooperate Card', icon: CreditCard },
      { value: 'Personal', icon: User },
    ];
  }
};

// Helper functions moved to RefundReportProvider
// getPerDiemRate is now in the provider

export const RefundReportIL = (): React.ReactNode => {
  const { currentTrip } = useTrips();
  const { updateExpense } = useExpenses(); // Keep for updating payment methods
  const { supabaseUser } = useAuth();
  const navigate = useNavigate();
  const { tripId } = useParams<{ tripId: string }>();
  const [userBusiness, setUserBusiness] = useState<Business | null>(null);

  // Get all data from RefundReportProvider
  const {
    flights,
    accommodations,
    deductibleExpenses,
    perDiems,
    loading,
    recalculatePerDiems,
    updateFlightDetails,
    retryConversion,
    updatePerDiemType,
    updatePerDiemDays,
  } = useRefundReport();

  // Calculate per diems when flights data is available
  // Using flights.length as dep to avoid infinite loop from recalculatePerDiems identity changes
  useEffect(() => {
    if (flights.length > 0) {
      recalculatePerDiems();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flights.length]);

  // IL Report is locked to ILS currency only
  const reportCurrency = 'ILS';

  // State for UI edits only (not business logic)
  const [perDiemEdits, setPerDiemEdits] = useState<Record<string, Partial<PerDiem>>>({});

  // Local state to prevent flickering - start with true, set to false once we have data
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Update isInitialLoad when data is available
  useEffect(() => {
    if (!loading && (flights.length > 0 || accommodations.length > 0 || deductibleExpenses.length > 0 || perDiems.length > 0)) {
      setIsInitialLoad(false);
    }
  }, [loading, flights.length, accommodations.length, deductibleExpenses.length, perDiems.length]);

  // Show skeleton if loading OR if it's the initial load
  const showSkeleton = loading || isInitialLoad;

  // State for refund calculations (UI-only)
  const [flightRefunds, setFlightRefunds] = useState<Record<string, number>>({});
  const [accommodationRefunds, setAccommodationRefunds] = useState<Record<string, number>>({});
  const [deductibleRefunds, setDeductibleRefunds] = useState<Record<string, number>>({});

  // State for accommodation edits (city and country)
  const [accommodationEdits, setAccommodationEdits] = useState<Record<string, { city?: string; country?: string }>>({});

  // Fetch user's business if they have one
  useEffect(() => {
    const fetchBusiness = async () => {
      if (supabaseUser?.business_id) {
        try {
          const business = await businessService.getBusinessById(supabaseUser.business_id);
          setUserBusiness(business);
        } catch (error) {
          console.error('Error fetching business:', error);
        }
      }
    };

    fetchBusiness();
  }, [supabaseUser?.business_id]);

  // Initialize refunds: 0 for flights and accommodations, full amount for deductible expenses
  useEffect(() => {
    const refunds: Record<string, number> = {};
    flights.forEach(flight => {
      refunds[flight.id] = 0; // Default to 0 for flights
    });
    setFlightRefunds(refunds);
  }, [flights]);

  useEffect(() => {
    const refunds: Record<string, number> = {};
    accommodations.forEach(acc => {
      refunds[acc.id] = 0; // Default to 0 for accommodations
    });
    setAccommodationRefunds(refunds);
  }, [accommodations]);

  useEffect(() => {
    const refunds: Record<string, number> = {};
    const onlyCooperateCard = currentTrip?.only_cooperate_card ?? true;

    deductibleExpenses.forEach(expense => {
      // Determine if this is a cooperate card payment
      const isCooperateCardPayment =
        (onlyCooperateCard && expense.paymentMethod === 'Card') ||
        (!onlyCooperateCard && expense.paymentMethod === 'Cooperate Card');

      // Default: 0 for cooperate card, full amount for other payment methods
      refunds[expense.id] = isCooperateCardPayment
        ? 0
        : convertToReportCurrency(expense.convertedAmount, expense.convertedCurrency);
    });
    setDeductibleRefunds(refunds);
  }, [deductibleExpenses, currentTrip?.only_cooperate_card]);

  // All data loading and business logic moved to RefundReportProvider

  // For IL Report, always use the stored ILS converted amounts
  const convertToReportCurrency = (
    convertedAmount: number,
    convertedCurrency: string
  ): number => {
    // IL Report shows ILS only - use stored converted amounts
    return convertedAmount;
  };

  // Check if an expense has a failed conversion (rate=1 but currency differs)
  const hasConversionFailed = (exchangeRate: number | null | undefined, originalCurrency: string, convertedCurrency: string): boolean => {
    return exchangeRate === 1 && originalCurrency !== convertedCurrency;
  };

  // Calculate trip days from expenses
  const calculateTripDays = (): number => {
    const allDates: Date[] = [];

    // Get dates from flights
    flights.forEach(flight => {
      if (flight.date) {
        allDates.push(new Date(flight.date));
      }
    });

    // Get dates from deductible expenses
    deductibleExpenses.forEach(expense => {
      if (expense.date) {
        allDates.push(new Date(expense.date));
      }
    });

    if (allDates.length === 0) return 0;

    const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

    const diffTime = Math.abs(maxDate.getTime() - minDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays + 1; // Include both start and end day
  };

  // Get per diems to display (with default if empty)
  const displayPerDiems = perDiems.length > 0
    ? perDiems
    : [{
        id: 'default',
        country: '',
        type: 'With Accommodation' as const,
        days: calculateTripDays(),
        dailyAmountUSD: 0,
        daily_rate_usd: 0,
        total_usd: 0,
        total_ils: 0,
        exchange_rate_usd_to_ils: null,
        calculation_date: null,
      }];

  // Handler for editing per diem cells
  const handlePerDiemEdit = async (rowIndex: number, columnKey: string, newValue: any) => {
    const perDiem = displayPerDiems[rowIndex];
    if (!perDiem) return;

    // Type changes go through the provider (handles merging and recalculation)
    if (columnKey === 'type') {
      await updatePerDiemType(perDiem.id, newValue as 'With Accommodation' | 'Self Accommodation');
      return;
    }

    // Days changes are persisted through the provider (0 removes the row).
    // Ignore an emptied cell so blurring a cleared input doesn't zero the record.
    if (columnKey === 'days') {
      if (newValue === '' || newValue === null || newValue === undefined) return;
      await updatePerDiemDays(perDiem.id, Number(newValue));
      return;
    }

    const updatedEdits = { ...perDiemEdits };

    if (!updatedEdits[perDiem.id]) {
      updatedEdits[perDiem.id] = {};
    }

    // Update the specific field
    if (columnKey === 'country') {
      updatedEdits[perDiem.id][columnKey] = newValue;
    }

    setPerDiemEdits(updatedEdits);
  };

  // Handler for editing flight cells
  const handleFlightEdit = async (rowIndex: number, columnKey: string, newValue: any) => {
    const flight = flights[rowIndex];
    if (!flight) return;
    if (!currentTrip?.id) return;

    try {
      if (columnKey === 'paymentMethod') {
        // Payment method updates ExpensesContext automatically
        await updateExpense(flight.id, { payment_method: newValue });
      } else if (columnKey === 'class') {
        // Optimistically update flight details in provider
        updateFlightDetails(flight.id, { flight_class: newValue });

        // Save to database
        await flightDetailsService.upsert({
          expense_id: flight.id,
          trip_id: currentTrip.id,
          flight_class: newValue,
        });
      } else if (columnKey === 'formattedDate') {
        // Optimistically update flight details in provider
        updateFlightDetails(flight.id, { actual_flight_date: newValue });

        // Save to database
        await flightDetailsService.upsert({
          expense_id: flight.id,
          trip_id: currentTrip.id,
          actual_flight_date: newValue,
        });
      }
    } catch (error) {
      console.error('Failed to update flight:', error);
      // Revert optimistic update by reloading from database
      if (columnKey === 'class' || columnKey === 'formattedDate') {
        const details = await flightDetailsService.getByExpenseId(flight.id);
        if (details) {
          updateFlightDetails(flight.id, details);
        }
      }
    }
  };

  // Handler for editing accommodation cells
  const handleAccommodationEdit = async (rowIndex: number, columnKey: string, newValue: any) => {
    const accommodation = accommodations[rowIndex];
    if (!accommodation) return;

    if (columnKey === 'paymentMethod') {
      try {
        // Payment method updates ExpensesContext automatically
        await updateExpense(accommodation.id, { payment_method: newValue });
      } catch (error) {
        console.error('Failed to update payment method:', error);
      }
    } else if (columnKey === 'city' || columnKey === 'country') {
      // Save city and country to database
      try {
        await updateExpense(accommodation.id, { [columnKey]: newValue });

        // Also update local state for immediate UI feedback
        const updatedEdits = { ...accommodationEdits };
        if (!updatedEdits[accommodation.id]) {
          updatedEdits[accommodation.id] = {};
        }
        updatedEdits[accommodation.id][columnKey] = newValue;
        setAccommodationEdits(updatedEdits);
      } catch (error) {
        console.error(`Failed to update ${columnKey}:`, error);
      }
    }
  };

  // Handler for editing deductible expense cells
  const handleDeductibleExpenseEdit = async (rowIndex: number, columnKey: string, newValue: any) => {
    const expense = deductibleExpenses[rowIndex];
    if (!expense) return;

    if (columnKey === 'paymentMethod') {
      try {
        // Payment method updates ExpensesContext automatically
        await updateExpense(expense.id, { payment_method: newValue });
      } catch (error) {
        console.error('Failed to update payment method:', error);
      }
    }
  };

  // Refund handlers for Flights
  const handleFlightRefundAll = () => {
    const refunds: Record<string, number> = {};
    flights.forEach(flight => {
      refunds[flight.id] = flight.convertedAmount;
    });
    setFlightRefunds(refunds);
  };

  const handleFlightRefundCapped = (maxAmount: number) => {
    const refunds: Record<string, number> = {};
    flights.forEach(flight => {
      refunds[flight.id] = Math.min(flight.convertedAmount, maxAmount);
    });
    setFlightRefunds(refunds);
  };

  const handleFlightRefundPartial = (percentage: number) => {
    const refunds: Record<string, number> = {};
    flights.forEach(flight => {
      refunds[flight.id] = flight.convertedAmount * (percentage / 100);
    });
    setFlightRefunds(refunds);
  };

  const handleFlightRefundZero = () => {
    const refunds: Record<string, number> = {};
    flights.forEach(flight => {
      refunds[flight.id] = 0;
    });
    setFlightRefunds(refunds);
  };

  // Refund handlers for Accommodations
  const handleAccommodationRefundAll = () => {
    const refunds: Record<string, number> = {};
    accommodations.forEach(acc => {
      refunds[acc.id] = acc.convertedAmount;
    });
    setAccommodationRefunds(refunds);
  };

  const handleAccommodationRefundCapped = (maxAmount: number) => {
    const refunds: Record<string, number> = {};
    accommodations.forEach(acc => {
      refunds[acc.id] = Math.min(acc.convertedAmount, maxAmount);
    });
    setAccommodationRefunds(refunds);
  };

  const handleAccommodationRefundPartial = (percentage: number) => {
    const refunds: Record<string, number> = {};
    accommodations.forEach(acc => {
      refunds[acc.id] = acc.convertedAmount * (percentage / 100);
    });
    setAccommodationRefunds(refunds);
  };

  const handleAccommodationRefundZero = () => {
    const refunds: Record<string, number> = {};
    accommodations.forEach(acc => {
      refunds[acc.id] = 0;
    });
    setAccommodationRefunds(refunds);
  };

  // Refund handlers for Deductible Expenses
  const handleDeductibleRefundAll = () => {
    const refunds: Record<string, number> = {};
    deductibleExpenses.forEach(expense => {
      refunds[expense.id] = expense.convertedAmount;
    });
    setDeductibleRefunds(refunds);
  };

  const handleDeductibleRefundCapped = (maxAmount: number) => {
    const refunds: Record<string, number> = {};
    deductibleExpenses.forEach(expense => {
      refunds[expense.id] = Math.min(expense.convertedAmount, maxAmount);
    });
    setDeductibleRefunds(refunds);
  };

  const handleDeductibleRefundPartial = (percentage: number) => {
    const refunds: Record<string, number> = {};
    deductibleExpenses.forEach(expense => {
      refunds[expense.id] = expense.convertedAmount * (percentage / 100);
    });
    setDeductibleRefunds(refunds);
  };

  const handleDeductibleRefundZero = () => {
    const refunds: Record<string, number> = {};
    deductibleExpenses.forEach(expense => {
      refunds[expense.id] = 0;
    });
    setDeductibleRefunds(refunds);
  };

  // Calculate totals for export (sum refund amounts, not full amounts)
  const flightTotal = flights.reduce((sum, flight) => sum + (flightRefunds[flight.id] || 0), 0);
  const accommodationTotal = accommodations.reduce((sum, acc) => sum + (accommodationRefunds[acc.id] || 0), 0);
  const deductibleTotal = deductibleExpenses.reduce((sum, exp) => sum + (deductibleRefunds[exp.id] || 0), 0);
  const perDiemTotal = perDiems.reduce((sum, pd) => sum + (pd.total_ils || 0), 0);
  const grandTotal = flightTotal + accommodationTotal + deductibleTotal + perDiemTotal;

  // Calculate total cost (sum of all converted expense amounts)
  const totalCost =
    flights.reduce((sum, flight) => sum + convertToReportCurrency(flight.convertedAmount, flight.convertedCurrency), 0) +
    accommodations.reduce((sum, acc) => sum + convertToReportCurrency(acc.convertedAmount, acc.convertedCurrency), 0) +
    deductibleExpenses.reduce((sum, exp) => sum + convertToReportCurrency(exp.convertedAmount, exp.convertedCurrency), 0) +
    perDiemTotal;

  // Format trip dates for export
  // Format dates as Ddd-Mmm-YY
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  const tripDates = currentTrip?.start_date && currentTrip?.end_date
    ? `${formatDate(currentTrip.start_date)} to ${formatDate(currentTrip.end_date)}`
    : undefined;

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6 sm:h-8 sm:w-8" />
            Refund Report (IL)
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">Tax refund report for {currentTrip?.nickname || 'your trip'}</p>
        </div>
        <ExportButtons
          tripName={currentTrip?.nickname || 'Trip'}
          tripDates={tripDates}
          tripReason={currentTrip?.description || undefined}
          flights={flights}
          accommodations={accommodations}
          deductibleExpenses={deductibleExpenses}
          perDiems={perDiems}
          currency={reportCurrency}
          flightTotal={flightTotal}
          accommodationTotal={accommodationTotal}
          deductibleTotal={deductibleTotal}
          perDiemTotal={perDiemTotal}
          grandTotal={grandTotal}
          totalCost={totalCost}
          businessName={userBusiness?.name}
          businessLogo={userBusiness?.logo || undefined}
        />
      </div>

      {showSkeleton ? (
        <>
          {/* Summary Skeleton */}
          <Card>
            <CardHeader className="pb-3">
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col items-center justify-center p-6 bg-muted/50 rounded-lg">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-9 w-40" />
                </div>
                <div className="flex flex-col items-center justify-center p-6 bg-primary/10 rounded-lg border-2 border-primary">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-9 w-40" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Loading Skeletons */}
          <RefundReportSkeleton
            title="Flights"
            description="Flight bookings and refund information"
            rows={3}
            columns={8}
          />
          <RefundReportSkeleton
            title="Accommodation"
            description="Hotel and lodging expenses"
            rows={3}
            columns={8}
          />
          <RefundReportSkeleton
            title="Deductible Expenses"
            description="Other documented expenses"
            rows={3}
            columns={7}
          />
          <RefundReportSkeleton
            title="Per Diem"
            description="Daily allowances by country"
            rows={2}
            columns={4}
          />

          {/* Totals Skeleton */}
          <Card>
            <CardHeader className="pb-3">
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between py-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <>
          {/* Summary Section */}
          <RefundReportSummary
            totalCost={totalCost}
            totalRefund={grandTotal}
            currency={reportCurrency}
          />

          {/* Flights Section */}
          <RefundReportSection
        title="Flights"
        description="Flight bookings and refund information"
        icon={Plane}
        buttonText="Add Your Flights"
        onButtonClick={() => navigate(`/trip/${tripId}/add-expense`)}
        columns={[
          { header: 'Flight', accessor: 'flight', align: 'left', width: '180px', primaryField: true },
          { header: 'Destination', accessor: 'destinationCountry', align: 'left', width: '120px' },
          { header: 'Date', accessor: 'formattedDate', align: 'left', width: '110px', editable: true, columnType: 'date' },
          { header: 'Class', accessor: 'class', align: 'left', width: '100px', editable: true, columnType: 'select', options: ['Economy', 'Premium Economy', 'Business', 'First Class'] },
          { header: 'Cost', accessor: 'cost', align: 'right', width: '100px', primaryField: true },
          { header: 'Currency', accessor: 'currency', align: 'left', width: '80px' },
          { header: `In ${reportCurrency}`, accessor: 'convertedCost', align: 'right', width: '100px', showTooltip: true, tooltipAccessor: 'exchangeRateTooltip' },
          { header: 'Payment Method', accessor: 'paymentMethod', align: 'left', width: '140px', editable: true, columnType: 'select-with-other', optionsWithIcons: getPaymentMethods(currentTrip?.only_cooperate_card ?? true) },
          { header: 'Refund', accessor: 'refund', align: 'right', width: '100px' },
        ]}
        data={flights.map(flight => ({
            ...flight,
            flight: flight.notes ? `${flight.flight} * ${flight.notes}` : flight.flight,
            formattedDate: flight.date, // Store ISO format for date picker
            convertedCost: convertToReportCurrency(flight.convertedAmount, flight.convertedCurrency),
            refund: flightRefunds[flight.id] || 0,
            exchangeRateTooltip: flight.exchangeRate && flight.conversionDate
              ? `Rate: ${flight.exchangeRate.toFixed(4)} on ${new Date(flight.conversionDate).toLocaleDateString()}`
              : undefined,
            _conversionFailed: hasConversionFailed(flight.exchangeRate, flight.currency, flight.convertedCurrency),
        }))}
        onCellEdit={handleFlightEdit}
        onRetryConversion={retryConversion}
        onRefundAll={handleFlightRefundAll}
        onRefundCapped={handleFlightRefundCapped}
        onRefundPartial={handleFlightRefundPartial}
        onRefundZero={handleFlightRefundZero}
      />

      {/* Accommodation Section */}
      <RefundReportSection
        title="Accommodation"
        description="Hotel and lodging expenses"
        icon={Hotel}
        buttonText="Add Accommodation"
        onButtonClick={() => navigate(`/trip/${tripId}/add-expense?category=Accommodation`)}
        columns={[
          { header: 'Description', accessor: 'description', align: 'left', width: '200px', primaryField: true },
          { header: 'City', accessor: 'city', align: 'left', width: '150px', editable: true, columnType: 'text' },
          { header: 'Country', accessor: 'country', align: 'left', width: '120px', editable: true, columnType: 'country' },
          { header: 'Nights', accessor: 'nights', align: 'right', width: '80px', primaryField: true },
          { header: 'Cost/Night', accessor: 'costPerNight', align: 'right', width: '100px' },
          { header: 'Currency', accessor: 'currency', align: 'left', width: '80px' },
          { header: `In ${reportCurrency}`, accessor: 'totalConverted', align: 'right', width: '100px', primaryField: true, showTooltip: true, tooltipAccessor: 'exchangeRateTooltip' },
          { header: 'Payment Method', accessor: 'paymentMethod', align: 'left', width: '140px', editable: true, columnType: 'select-with-other', optionsWithIcons: getPaymentMethods(currentTrip?.only_cooperate_card ?? true) },
          { header: 'Refund', accessor: 'refund', align: 'right', width: '100px' },
        ]}
        data={accommodations.map(acc => {
          const edits = accommodationEdits[acc.id] || {};
          const city = edits.city !== undefined ? edits.city : acc.city;
          const country = edits.country !== undefined ? edits.country : acc.country;

          return {
            ...acc,
            description: acc.notes ? `${acc.description} * ${acc.notes}` : acc.description,
            city: city,
            country: country,
            totalConverted: convertToReportCurrency(acc.convertedAmount, acc.convertedCurrency),
            refund: accommodationRefunds[acc.id] || 0,
            exchangeRateTooltip: acc.exchangeRate && acc.conversionDate
              ? `Rate: ${acc.exchangeRate.toFixed(4)} on ${new Date(acc.conversionDate).toLocaleDateString()}`
              : undefined,
            _conversionFailed: hasConversionFailed(acc.exchangeRate, acc.currency, acc.convertedCurrency),
          };
        })}
        onCellEdit={handleAccommodationEdit}
        onRetryConversion={retryConversion}
        onRefundAll={handleAccommodationRefundAll}
        onRefundCapped={handleAccommodationRefundCapped}
        onRefundPartial={handleAccommodationRefundPartial}
        onRefundZero={handleAccommodationRefundZero}
      />

      {/* Deductible Expenses Section */}
      <RefundReportSection
        title="Deductible Expenses"
        description="Other documented expenses"
        icon={Receipt}
        buttonText="Add Deductible"
        onButtonClick={() => navigate(`/trip/${tripId}/add-expense`)}
        columns={[
          { header: 'Description', accessor: 'description', align: 'left', width: '200px', primaryField: true },
          { header: 'Date', accessor: 'formattedDate', align: 'left', width: '120px' },
          { header: 'Amount', accessor: 'amount', align: 'right', width: '100px', primaryField: true },
          { header: 'Currency', accessor: 'currency', align: 'left', width: '80px' },
          { header: `In ${reportCurrency}`, accessor: 'convertedAmount', align: 'right', width: '100px', showTooltip: true, tooltipAccessor: 'exchangeRateTooltip' },
          { header: 'Payment Method', accessor: 'paymentMethod', align: 'left', width: '140px', editable: true, columnType: 'select-with-other', optionsWithIcons: getPaymentMethods(currentTrip?.only_cooperate_card ?? true) },
          { header: 'Refund', accessor: 'refund', align: 'right', width: '100px' },
        ]}
        data={deductibleExpenses.map(expense => ({
            ...expense,
            description: expense.notes ? `${expense.description} * ${expense.notes}` : expense.description,
            formattedDate: new Date(expense.date).toLocaleDateString(),
            convertedAmount: convertToReportCurrency(expense.convertedAmount, expense.convertedCurrency),
            refund: deductibleRefunds[expense.id] || 0,
            exchangeRateTooltip: expense.exchangeRate && expense.conversionDate
              ? `Rate: ${expense.exchangeRate.toFixed(4)} on ${new Date(expense.conversionDate).toLocaleDateString()}`
              : undefined,
            _conversionFailed: hasConversionFailed(expense.exchangeRate, expense.currency, expense.convertedCurrency),
        }))}
        onCellEdit={handleDeductibleExpenseEdit}
        onRetryConversion={retryConversion}
        onRefundAll={handleDeductibleRefundAll}
        onRefundCapped={handleDeductibleRefundCapped}
        onRefundPartial={handleDeductibleRefundPartial}
        onRefundZero={handleDeductibleRefundZero}
      />

      {/* Per Diem Section - Always visible */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-2 flex-1">
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Per Diem
              </CardTitle>
              <CardDescription>Daily allowances by country (Israeli tax regulations)</CardDescription>
            </div>
          </div>
        </CardHeader>
          <CardContent>
            {/* Desktop view - Table */}
            <div className="hidden sm:block">
              <SpreadsheetTable
                columns={[
                  { header: 'Country', accessor: 'country', align: 'left', width: '180px', editable: true, columnType: 'country' },
                  { header: 'Type', accessor: 'type', align: 'left', width: '160px', editable: true, columnType: 'select', options: ['With Accommodation', 'Self Accommodation'] },
                  { header: 'Days', accessor: 'days', align: 'right', width: '80px', editable: true, columnType: 'number' },
                  { header: 'Daily Amount (USD)', accessor: 'dailyAmountUSD', align: 'right', width: '140px', tooltipRender: (displayValue) => <PerDiemRatesInfo displayValue={displayValue} /> },
                  { header: 'Total (USD)', accessor: 'totalUSD', align: 'right', width: '120px' },
                  { header: `Total (${reportCurrency})`, accessor: 'totalILS', align: 'right', width: '120px' },
                ]}
                data={displayPerDiems.map(perDiem => {
                // Apply any edits to this per diem (days edits persist via the provider)
                const edits = perDiemEdits[perDiem.id] || {};
                const days = perDiem.days;

                // Use stored daily rate (calculated by provider)
                const dailyAmountUSD = perDiem.dailyAmountUSD;
                const totalUSD = days * dailyAmountUSD;

                // Use stored ILS value with historical exchange rate
                const totalILS = perDiem.total_ils !== null && perDiem.total_ils !== undefined
                  ? perDiem.total_ils.toFixed(2)
                  : '0.00';

                return {
                  ...perDiem,
                  country: edits.country !== undefined ? edits.country : perDiem.country,
                  type: perDiem.type,
                  days,
                  dailyAmountUSD,
                  totalUSD: totalUSD.toFixed(2),
                  totalILS
                };
              })}
              onCellEdit={handlePerDiemEdit}
            />
            </div>

            {/* Mobile view - Cards */}
            <div className="sm:hidden space-y-3">
              {displayPerDiems.map((perDiem, index) => {
                const days = perDiem.days;
                const dailyAmountUSD = perDiem.dailyAmountUSD;
                const totalUSD = days * dailyAmountUSD;
                const totalILS = perDiem.total_ils !== null && perDiem.total_ils !== undefined
                  ? perDiem.total_ils.toFixed(2)
                  : '0.00';

                return (
                  <RefundItemCard
                    key={index}
                    item={{
                      ...perDiem,
                      days,
                      dailyAmountUSD,
                      totalUSD: totalUSD.toFixed(2),
                      totalILS
                    }}
                    columns={[
                      { header: 'Country', accessor: 'country', align: 'left', primaryField: true },
                      { header: 'Type', accessor: 'type', align: 'left', primaryField: true },
                      { header: 'Days', accessor: 'days', align: 'right', primaryField: true },
                      { header: `Total (${reportCurrency})`, accessor: 'totalILS', align: 'right', primaryField: true },
                      { header: 'Daily Amount (USD)', accessor: 'dailyAmountUSD', align: 'right' },
                      { header: 'Total (USD)', accessor: 'totalUSD', align: 'right' },
                    ]}
                    index={index}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Totals Breakdown */}
        <RefundReportTotals
          flightTotal={flightTotal}
          accommodationTotal={accommodationTotal}
          deductibleTotal={deductibleTotal}
          perDiemTotal={perDiemTotal}
          grandTotal={grandTotal}
          currency={reportCurrency}
        />
        </>
      )}
    </div>
  );
};
